import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useApi } from "./useApi";
import { apiErrorMessage } from "../utils/apiError";
import { OrderSearchBody, OrderSearchQuery, OrderSearchResult } from "../types/evidenceBankCase";

// A POST is never superseded by useApi, so a late answer would land on whatever the agent moved on
// to. Every search and every reset takes the next number, and only the latest one may land.
const BANK_SEARCH_PATH = "/api/evidence-bank-cases/order-search";

export function useOrderSearch(path: string = BANK_SEARCH_PATH) {
  const { post } = useApi();
  const latest = useRef(0);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [found, setFound] = useState<{ result: OrderSearchResult; by: OrderSearchBody["by"] } | null>(null);

  const resetSearch = useCallback(() => {
    latest.current += 1;
    setSearching(false);
    setSearchError(null);
    setFound(null);
  }, []);

  // Another endpoint is another dispute: its results, and any answer still in flight, are not this one's.
  useEffect(() => resetSearch, [path, resetSearch]);

  const findOrder = useCallback(async (body: OrderSearchBody | OrderSearchQuery) => {
    const issued = ++latest.current;
    setSearching(true);
    setSearchError(null);
    setFound(null);
    try {
      const result = await post<OrderSearchResult>(path, body, { slowApi: true });
      if (issued === latest.current) setFound({ result, by: body.by });
    } catch (err: any) {
      // A 400 is the API declining what was typed: its message is the answer.
      if (err?.status !== 400) Sentry.captureException(err);
      if (issued === latest.current) {
        setSearchError(err?.status === 400 ? apiErrorMessage(err, "The search failed. Try again.") : "The search failed. Try again.");
      }
    } finally {
      if (issued === latest.current) setSearching(false);
    }
  }, [post, path]);

  return { searching, searchError, found, findOrder, resetSearch };
}
